import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  BookOpenCheck, 
  CheckCircle, 
  Calendar, 
  ArrowRight,
  Loader2,
  Clock,
  ExternalLink,
  Video,
  FileText,
  Brain
} from 'lucide-react';

const HomeworkWidget = () => {
  const [currentHomework, setCurrentHomework] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchCurrentHomework();
  }, []);

  const fetchCurrentHomework = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const apiUrl = `${import.meta.env.VITE_API_URL}/api/homework/current`;
      
      console.log('HomeworkWidget: Fetching from:', apiUrl);
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('HomeworkWidget: Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Homework widget response:', data);
        setCurrentHomework(data.data.homework);
        if (!data.data.homework) {
          console.log('No homework found in response - data.data:', data.data);
        }
      } else {
        console.error('Failed to fetch current homework:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('Error fetching current homework:', error);
      console.error('Error stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!currentHomework) return;
    
    try {
      setCompleting(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/homework/${currentHomework.id}/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Trigger confetti animation
        triggerConfetti();
        // Update state
        setCurrentHomework({...currentHomework, is_completed: true});
      }
    } catch (error) {
      console.error('Error marking homework complete:', error);
    } finally {
      setCompleting(false);
    }
  };

  const triggerConfetti = () => {
    // Simple confetti effect using CSS animations
    const confetti = document.createElement('div');
    confetti.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;
    
    // Create confetti particles
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 10px;
        height: 10px;
        background: ${['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)]};
        left: ${Math.random() * 100}%;
        top: -10px;
        animation: confetti-fall 3s linear forwards;
        border-radius: 50%;
      `;
      confetti.appendChild(particle);
    }
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes confetti-fall {
        0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(confetti);
    
    // Remove after animation
    setTimeout(() => {
      document.body.removeChild(confetti);
      document.head.removeChild(style);
    }, 3000);
  };

  const getContentIcon = (linkType) => {
    switch (linkType) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'resource': return <FileText className="w-4 h-4" />;
      case 'prompt': return <Brain className="w-4 h-4" />;
      case 'external': return <ExternalLink className="w-4 h-4" />;
      default: return <BookOpenCheck className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer rounded-3xl border-0 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentHomework) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer rounded-3xl border-0 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg">
        <Link to="/homework">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <BookOpenCheck className="w-7 h-7 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-gray-900">This Week's Homework</CardTitle>
                  <CardDescription className="text-gray-600">Weekly assignments and tasks</CardDescription>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpenCheck className="w-8 h-8 text-purple-400" />
              </div>
              <p className="text-gray-600 mb-2 text-lg">No homework this week! 😎</p>
              <p className="text-gray-500">Check back next week for new assignments</p>
            </div>
            <Button className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
              <BookOpenCheck className="w-5 h-5 mr-2" />
              View All Homework
            </Button>
          </CardContent>
        </Link>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer rounded-3xl border-0 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <BookOpenCheck className="w-7 h-7 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">This Week's Homework</CardTitle>
              <CardDescription className="text-gray-600">Due {formatDate(currentHomework.due_date)}</CardDescription>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {currentHomework.is_completed ? (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Complete
              </Badge>
            ) : (
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                <Clock className="w-3 h-3 mr-1" />
                Pending
              </Badge>
            )}
            <ArrowRight className="w-6 h-6 text-purple-600" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">{currentHomework.title}</h3>
          <p className="text-gray-600 text-sm mb-3">
            {truncateText(currentHomework.description, 120)}
          </p>
          
          {currentHomework.content_links && currentHomework.content_links.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Resources:</p>
              <div className="flex flex-wrap gap-2">
                {currentHomework.content_links.slice(0, 3).map((link, index) => (
                  <div key={index} className="flex items-center space-x-1 bg-white px-2 py-1 rounded-lg text-xs text-gray-600">
                    {getContentIcon(link.link_type)}
                    <span>{truncateText(link.link_text, 20)}</span>
                  </div>
                ))}
                {currentHomework.content_links.length > 3 && (
                  <div className="bg-white px-2 py-1 rounded-lg text-xs text-gray-500">
                    +{currentHomework.content_links.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex space-x-3">
          <Link to="/homework" className="flex-1">
            <Button variant="outline" className="w-full h-10 rounded-xl border-purple-200 text-purple-700 hover:bg-purple-50">
              View Details
            </Button>
          </Link>
          
          {!currentHomework.is_completed && (
            <Button 
              onClick={handleMarkComplete}
              disabled={completing}
              className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white h-10 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {completing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {completing ? 'Marking...' : 'Mark Done'}
            </Button>
          )}
          
          {currentHomework.is_completed && (
            <div className="flex-1 flex items-center justify-center bg-green-100 text-green-800 rounded-xl h-10">
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete!
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HomeworkWidget;
